import { useState } from "react";
import { Link } from "react-router-dom";

const socials = [
  { icon: "ri-twitter-x-fill", label: "X", href: "https://x.com" },
  { icon: "ri-youtube-fill", label: "YouTube", href: "https://youtube.com" },
  { icon: "ri-instagram-fill", label: "Instagram", href: "https://instagram.com" },
  { icon: "ri-facebook-fill", label: "Facebook", href: "https://facebook.com" },
  { icon: "ri-linkedin-fill", label: "LinkedIn", href: "https://linkedin.com" },
];

export default function MiradasHero() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleCtaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.querySelector("#citas");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative w-full h-[580px] sm:h-[640px] md:h-[720px] lg:h-[760px] overflow-hidden">
      {/* Background image */}
      <img
        src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/763796c63600d531925ec6ca3ade5c93.png"
        alt="Miradas hero background"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* Multi-layer overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-700/60 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/25 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-l from-orange-500/30 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-black/10" />

      {/* Main hero content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-4 md:px-8 lg:px-12">
        {/* Social icons - left vertical column (hidden on small mobile) */}
        <div className="hidden md:flex absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 flex-col gap-3 lg:gap-4 z-20">
          {socials.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.label}
              className="w-10 h-10 lg:w-11 lg:h-11 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white hover:scale-110 flex items-center justify-center text-white text-base lg:text-lg transition-all duration-300 cursor-pointer backdrop-blur-sm"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={social.icon}></i>
              </div>
            </a>
          ))}
        </div>

        {/* Center text */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="font-utendo text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight drop-shadow-lg">
            Miradas
          </h1>
          <h2 className="font-utendo text-white text-lg sm:text-xl md:text-2xl lg:text-3xl font-normal mt-2 md:mt-3 drop-shadow-md">
            #VocesQueConstruyenSalud
          </h2>
          <p className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed max-w-xl mx-auto mt-4 md:mt-5 drop-shadow-md"
          style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
            Distintas voces del territorio comparten su visión
            <br className="hidden sm:block" />
            sobre los desafíos de la salud en Tarapacá.
          </p>

          {/* CTA Button - orange filled */}
          <a
            href="#citas"
            onClick={handleCtaClick}
            className="inline-flex items-center justify-center mt-6 md:mt-8 px-8 md:px-10 py-3 md:py-4 bg-primary hover:bg-primary-dark text-white text-sm md:text-base rounded-full transition-colors duration-200 shadow-lg cursor-pointer"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
            Conoce más
            <i className="ri-arrow-right-line ml-2 text-sm"></i>
          </a>
        </div>
      </div>

      {/* Bottom institutional block */}
      <div className="absolute bottom-4 md:bottom-6 lg:bottom-8 left-0 right-0 z-20 px-4 md:px-8 lg:px-12">
        <div className="text-center max-w-4xl mx-auto">
          <p className="text-white text-xs sm:text-sm md:text-base leading-relaxed drop-shadow-md"
          style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
            Proyecto financiado por el Gobierno Regional de Tarapacá y su Consejo Regional
            a través del Fondo Regional para la Productividad y Desarrollo (FRPD).
          </p>

          {/* Logos row */}
          <div className="mt-3 md:mt-4 flex flex-wrap items-center justify-center gap-3 md:gap-5 lg:gap-8">
            {/* FINANCIA */}
            <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
              FINANCIA:
            </span>
            <img
              src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/6f26c859-137b-413e-b0d0-cc2dccfa176d_GORE-APAIZADO-BLANCO.png?v=fabb07f42abe3733fb8c182bec9ed063"
              alt="Gobierno Regional de Tarapacá"
              className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain drop-shadow-md"
              loading="lazy"
            />
            <img
              src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/ce295166-fa77-4f96-937c-1b07cf72d02d_CORE-APAIZADO-BLANCO.png?v=d319fb4b6e916e03c270738313aa48bb"
              alt="CORE Tarapacá"
              className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md"
              loading="lazy"
            />

            {/* EJECUTA */}
            <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
              EJECUTA:
            </span>
            <img
              src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/c24cb77b-800b-4f84-b0ee-1bfaff0da8b8_UNAP-APAIZADO-BLANCO.png?v=6ba61374883a59863a9baa8030e04d98"
              alt="UNAP Universidad Arturo Prat"
              className="h-8 sm:h-9 md:h-10 lg:h-12 w-auto object-contain drop-shadow-md"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}